import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { PageTitle } from '../../../_metronic/layout/core';
import { useAppData } from '../../providers/AppDataProvider';
import { InvoiceProvider, useInvoice } from '../invoices/contexts/InvoiceContext';
import { BillsProvider, useBills } from '../bills/contexts/BillsContext';
import { RepeatingInvoiceProvider } from '../invoices/contexts/RepeatingInvoiceContext';
import { CreditNoteProvider } from '../invoices/contexts/CreditNoteContext';
import { Bill } from '../../types/accounting';
import { Invoice } from '../../types/accounting';

import InvoiceFilters from '../invoices/components/InvoiceFilters';
import InvoiceList from '../invoices/components/InvoiceList';
import CustomerInvoiceHistory from '../invoices/components/CustomerInvoiceHistory';
import CreateInvoiceModal from '../invoices/components/CreateInvoiceModal';
import CreditNotesModal from '../invoices/components/CreditNotesModal';

import BillFilters from '../bills/components/BillFilters';
import BillList from '../bills/components/BillList';
import CreateBillModal from '../bills/components/CreateBillModal';

import RepeatingInvoiceList from '../invoices/components/RepeatingInvoiceList';

import CashFlowSummary from './CashFlowSummary';
// RepairOrphanedContactsCard removed from this page 2026-05 — kept in
// the codebase for potential reuse on a dedicated maintenance page.

import './AccountingPage.scss';

const accountingBreadCrumbs = [
  { title: 'Dashboard', path: '/dashboard', isSeparator: false, isActive: false },
  { title: '', path: '', isSeparator: true, isActive: false },
];

type ViewType = 'invoices' | 'bills' | 'recurring';

interface ViewToggleProps {
  activeView: ViewType;
  onChange: (view: ViewType) => void;
}

function ViewToggle({ activeView, onChange }: ViewToggleProps) {
  return (
    <div className="accounting-view-toggle accounting-view-toggle-three">
      <button
        className={`toggle-option ${activeView === 'invoices' ? 'active invoices' : ''}`}
        onClick={() => onChange('invoices')}
      >
        <i className="bi bi-receipt me-2"></i>
        Invoices
        <span className="toggle-subtitle">Money In</span>
      </button>
      <button
        className={`toggle-option ${activeView === 'bills' ? 'active bills' : ''}`}
        onClick={() => onChange('bills')}
      >
        <i className="bi bi-file-text me-2"></i>
        Bills
        <span className="toggle-subtitle">Money Out</span>
      </button>
      <button
        className={`toggle-option ${activeView === 'recurring' ? 'active recurring' : ''}`}
        onClick={() => onChange('recurring')}
      >
        <i className="bi bi-arrow-repeat me-2"></i>
        Recurring
        <span className="toggle-subtitle">Templates</span>
      </button>
      <div className={`toggle-slider ${activeView}`} />
    </div>
  );
}

function InvoicesContent() {
  const { setInvoiceFilters, fetchInvoices } = useInvoice();

  const [selectedContact, setSelectedContact] = useState<{ id: string; name: string } | null>(null);
  const [showCreditNotesModal, setShowCreditNotesModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [templateInvoice, setTemplateInvoice] = useState<Invoice | null>(null);

  const handleSelectContact = (contactId: string, contactName: string) => {
    setSelectedContact({ id: contactId, name: contactName });
  };

  const handleCloseContactHistory = () => {
    setSelectedContact(null);
    setInvoiceFilters({ contactId: undefined });
    fetchInvoices({ contactId: undefined });
  };

  const handleNewInvoice = () => {
    setEditingInvoice(null);
    setTemplateInvoice(null);
    setShowInvoiceModal(true);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setTemplateInvoice(null);
    setShowInvoiceModal(true);
  };

  const handleCopyInvoice = (invoice: Invoice) => {
    setEditingInvoice(null);
    setTemplateInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const handleCloseInvoiceModal = () => {
    setShowInvoiceModal(false);
    setEditingInvoice(null);
    setTemplateInvoice(null);
  };

  return (
    <>
      <InvoiceList
        filterContent={
          <InvoiceFilters
            embedded
            onOpenCreditNotes={() => setShowCreditNotesModal(true)}
          />
        }
        onSelectContact={handleSelectContact}
        onEditInvoice={handleEditInvoice}
        onCopyInvoice={handleCopyInvoice}
        onNewInvoice={handleNewInvoice}
      />

      <Modal
        show={!!selectedContact}
        onHide={handleCloseContactHistory}
        size="xl"
        centered
        scrollable
        fullscreen="md-down"
        className="customer-history-modal"
      >
        <Modal.Body className="p-0">
          {selectedContact && (
            <CustomerInvoiceHistory
              contactId={selectedContact.id}
              contactName={selectedContact.name}
              onClose={handleCloseContactHistory}
            />
          )}
        </Modal.Body>
      </Modal>

      <CreditNotesModal
        show={showCreditNotesModal}
        onHide={() => setShowCreditNotesModal(false)}
      />

      <CreateInvoiceModal
        show={showInvoiceModal}
        onHide={handleCloseInvoiceModal}
        editInvoice={editingInvoice}
        templateInvoice={templateInvoice}
      />
    </>
  );
}

function BillsContent() {
  const [showBillModal, setShowBillModal] = useState(false);
  const [editingBill, setEditingBill] = useState<Bill | null>(null);

  const handleNewBill = () => {
    setEditingBill(null);
    setShowBillModal(true);
  };

  const handleEditBill = (bill: Bill) => {
    setEditingBill(bill);
    setShowBillModal(true);
  };

  const handleCloseBillModal = () => {
    setShowBillModal(false);
    setEditingBill(null);
  };

  return (
    <>
      <BillList
        filterContent={<BillFilters embedded />}
        onEditBill={handleEditBill}
        onNewBill={handleNewBill}
      />

      <CreateBillModal
        show={showBillModal}
        onHide={handleCloseBillModal}
        editBill={editingBill}
      />
    </>
  );
}

function AccountingContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { xeroConnected: accountingReady } = useAppData();

  const viewParam = searchParams.get('view') as ViewType | null;
  const [activeView, setActiveView] = useState<ViewType>(
    viewParam === 'bills' ? 'bills' : viewParam === 'recurring' ? 'recurring' : 'invoices'
  );

  const handleViewChange = (view: ViewType) => {
    setActiveView(view);
    setSearchParams({ view });
  };

  useEffect(() => {
    const view = searchParams.get('view') as ViewType | null;
    if (view === 'bills' || view === 'invoices' || view === 'recurring') {
      setActiveView(view);
    }
  }, [searchParams]);

  if (!accountingReady) {
    return (
      <div className="card">
        <div className="card-body py-10 text-center">
          <span className="spinner-border text-primary" />
          <div className="text-muted mt-3">Loading accounting…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="accounting-page">
      <div className="accounting-header card mb-4 mb-md-5">
        <div className="card-body py-4 d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3">
          <div className="order-1 order-md-0">
            <h2 className="mb-0 fw-bold">Accounting</h2>
            <span className="text-muted fs-7">Manage your invoices and bills</span>
          </div>
          <div className="order-2 order-md-1 w-100 w-md-auto">
            <ViewToggle activeView={activeView} onChange={handleViewChange} />
          </div>
        </div>
      </div>

      <CashFlowSummary />

      <div className={`view-content ${activeView}`}>
        {activeView === 'invoices' && <InvoicesContent />}
        {activeView === 'bills' && <BillsContent />}
        {activeView === 'recurring' && <RepeatingInvoiceList />}
      </div>
    </div>
  );
}

export function AccountingPage() {
  return (
    <InvoiceProvider>
      <BillsProvider>
        <RepeatingInvoiceProvider>
          <CreditNoteProvider>
            <PageTitle breadcrumbs={accountingBreadCrumbs}>Accounting</PageTitle>
            <AccountingContent />
          </CreditNoteProvider>
        </RepeatingInvoiceProvider>
      </BillsProvider>
    </InvoiceProvider>
  );
}

export default AccountingPage;
